import type * as React from 'react'
import knight from '@/assets/pieces/wN.svg'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

/**
 * The masthead: mark, product, what this page is, who you are.
 *
 * The mark is the knight from the same piece set the diagrams use, so the one
 * piece of decoration in the app is drawn from the material rather than picked
 * out of an icon library.
 */
export function AppShell({
  subtitle,
  identity,
  children,
  right,
}: {
  subtitle?: string
  identity?: { initials: string; name: string; detail?: string | null }
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="mx-auto flex h-14 w-full max-w-[1460px] items-center gap-3 px-5 sm:px-7">
          <div className="flex items-center gap-2.5">
            <span className="flex size-7 items-center justify-center rounded-lg bg-primary">
              <img src={knight} alt="" className="size-5" />
            </span>
            <span className="font-semibold tracking-tight whitespace-nowrap">
              Osie Stylu: Feedback
            </span>
          </div>
          {subtitle ? (
            <>
              <Separator orientation="vertical" className="h-5" />
              <span className="truncate text-sm text-muted-foreground">{subtitle}</span>
            </>
          ) : null}
          <div className="flex-1" />
          {right}
          {identity ? (
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
                {identity.initials}
              </span>
              <span className="hidden sm:block">
                <span className="block text-sm leading-tight font-medium">
                  {identity.name}
                </span>
                {identity.detail ? (
                  <span className="block text-xs leading-tight text-muted-foreground">
                    {identity.detail}
                  </span>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      </header>
      <main className="flex-1">{children}</main>
    </div>
  )
}

export function PageBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('mx-auto w-full max-w-[1460px] px-5 py-8 sm:px-7', className)}
      {...props}
    />
  )
}

export function initialsOf(nick: string): string {
  const parts = nick.trim().split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
  }
  return nick.slice(0, 2).toUpperCase()
}
